import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useRBAC } from "../useRBAC";

// =============================================================================
// useMvpVoting — MVP peer voting for prestige system
// =============================================================================
// Wraps get_mvp_voting_status RPC + cast_mvp_vote RPC + teammate query.
// Exec bypass: execs see the UI anytime and can vote outside the window.
// Reps only see it during voting window (last 2 days of month).
// =============================================================================

export interface MvpVotingStatus {
  month: string;
  voting_open: boolean;
  days_until_open: number;
  total_eligible: number;
  total_voted: number;
  // Voter identities removed 2026-04-28 — anonymous per spec.
  // Caller-scoped fields replace voters[] for myVote UX:
  has_voted: boolean;
  my_nominee_id: string | null;
  my_nominee_name: string | null;
  tallies: MvpTally[];
}

export interface MvpTally {
  nominee_id: string;
  nominee_name: string;
  votes: number;
}

export interface Teammate {
  user_id: string;
  full_name: string;
  role: string;
  pod_id: string | null;
}

interface CastVoteResult {
  success: boolean;
  message?: string;
  error?: string;
  nominee_name?: string;
}

export function useMvpVoting(orgId: string | null) {
  const { user } = useAuth();
  const { hasTunnelVision } = useRBAC();

  const [votingStatus, setVotingStatus] = useState<MvpVotingStatus | null>(null);
  const [teammates, setTeammates] = useState<Teammate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch voting status + teammates
  const refresh = useCallback(async () => {
    if (!orgId || !user?.id || !supabase) return;
    setIsLoading(true);
    setError(null);

    try {
      // Parallel: voting status + teammate list
      const [statusRes, teammatesRes] = await Promise.all([
        supabase.rpc("get_mvp_voting_status", {
          p_organization_id: orgId,
          p_month: null,
        }),
        supabase
          .from("organization_members_readable")
          .select("user_id, user_name, role, pod_id")
          .eq("organization_id", orgId)
          .eq("status", "active")
          .in("role", ["closer", "setter", "dialer"]),
      ]);

      if (statusRes.error) throw new Error(statusRes.error.message);
      if (teammatesRes.error) throw new Error(teammatesRes.error.message);

      setVotingStatus(statusRes.data as MvpVotingStatus);

      // Map from organization_members_readable (user_name) to Teammate shape
      const mapped: Teammate[] = (teammatesRes.data || []).map(
        (m: { user_id: string; user_name: string; role: string; pod_id: string | null }) => ({
          user_id: m.user_id,
          full_name: m.user_name,
          role: m.role,
          pod_id: m.pod_id,
        })
      );
      setTeammates(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load voting data");
    } finally {
      setIsLoading(false);
    }
  }, [orgId, user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Current user's vote (if any) — derived from caller-scoped RPC fields (not voters[])
  const myVote = useMemo(() => {
    if (!votingStatus) return null;
    if (!votingStatus.has_voted || !votingStatus.my_nominee_id) return null;
    return {
      nominee_id: votingStatus.my_nominee_id,
      nominee_name: votingStatus.my_nominee_name ?? "",
    };
  }, [votingStatus]);

  // Eligible teammates (exclude self) — filter only, no shuffle yet
  const eligibleTeammates = useMemo(() => {
    if (!user?.id) return [];
    return teammates.filter((t) => t.user_id !== user.id);
  }, [teammates, user?.id]);

  // Per-mount seed — initialized ONCE via lazy useState.
  // Deterministic shuffle: same seed = same order across re-renders,
  // new mount = new seed = new shuffle. Prevents ballot reorder mid-decision.
  const [shuffleSeed] = useState(() => Math.random());

  const shuffledTeammates = useMemo(() => {
    if (eligibleTeammates.length === 0) return [];
    // Mulberry32 PRNG seeded once per mount
    const arr = [...eligibleTeammates];
    let s = Math.floor(shuffleSeed * 2 ** 32);
    for (let i = arr.length - 1; i > 0; i--) {
      s = (s + 0x6d2b79f5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      const rand = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      const j = Math.floor(rand * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [eligibleTeammates, shuffleSeed]);

  // Should show voting UI?
  // Phase 1 rollout: exec-only during voting window. Reps hidden until manual rollout.
  // When ready to show reps: change `false` below to `votingStatus.voting_open`
  const isVotingVisible = useMemo(() => {
    if (!votingStatus) return false;
    if (!hasTunnelVision && votingStatus.voting_open) return true; // exec sees it during window
    return false; // reps: hidden until manual rollout
  }, [votingStatus, hasTunnelVision]);

  // Cast vote
  const castVote = useCallback(
    async (nomineeId: string): Promise<CastVoteResult> => {
      if (!orgId || !supabase) return { success: false, error: "No organization" };
      const { data, error: rpcError } = await supabase.rpc("cast_mvp_vote", {
        p_organization_id: orgId,
        p_nominee_id: nomineeId,
      });
      if (rpcError) return { success: false, error: rpcError.message };
      // Refresh after casting to update local state
      await refresh();
      return data as CastVoteResult;
    },
    [orgId, refresh]
  );

  return {
    votingStatus,
    teammates: shuffledTeammates,
    myVote,
    castVote,
    isVotingVisible,
    isLoading,
    error,
    refresh,
  };
}
